import React, { useState, useEffect, useRef, useCallback } from 'react';
import EmojiPicker, { EmojiStyle, EmojiClickData, Theme } from 'emoji-picker-react';
import { apiFetch } from '../services/api';
import type { AuthUser } from '../hooks/useAuth';
import { BottomNav } from '../components/BottomNav';
import { BackButton } from '../components/BackButton';
import { AuthenticatedImage } from '../components/AuthenticatedImage';

interface Props { user: AuthUser; }

interface ConvUser {
  id: string;
  user_name: string;
  user_role: string;
  employee_code: string;
  avatar_path?: string | null;
  vessel_id?: string | null;
  vessel_name?: string | null;
  last_message?: string | null;
  last_at?: string | null;
  last_type?: string | null;
  unread_count?: number;
  unread_urgent?: number;
  unread_announcement?: number;
}

const STAMPS = [
  '😊','😂','🥹','😍','😎','😅',
  '😭','😤','🤔','😴','🤗','🥳',
  '👍','👎','👏','🙏','💪','🫡',
  '❤️','🔥','✨','💯','🎉','✅',
  '🚢','⚓','🌊','⛵','🌅','🐋',
];

interface Message {
  id: string;
  sender_user_id: string;
  sender_name: string;
  content: string;
  message_type: string;
  is_read?: boolean;
  created_at: string;
  edited_at?: string | null;
  deleted_at?: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  system_admin: 'システム管理者',
  shore_admin:  '陸上管理者',
  captain:      '船長',
  crew:         '船員',
  admin:        '管理者',
  system:       'システム',
};

function avatarBg(role: string) {
  if (['system_admin', 'shore_admin', 'admin'].includes(role)) return 'linear-gradient(135deg, #1a237e, #3949ab)';
  if (role === 'captain') return 'linear-gradient(135deg, #b45309, #d97706)';
  return 'linear-gradient(135deg, #0ea5e9, #6366f1)';
}

function UserAvatar({ name, role, avatarPath, size = 48 }: { name: string; role: string; avatarPath?: string | null; size?: number }) {
  return (
    <AuthenticatedImage
      resource={avatarPath ? `avatars/${avatarPath}` : null}
      alt={name}
      imgStyle={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      fallback={
        <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: avatarBg(role), display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: Math.round(size * 0.375) }}>
          {name.slice(0, 1)}
        </div>
      }
    />
  );
}

// SQLiteのdatetime('now')はUTCだがTZマーカーなし → 'Z'を付与してUTCとして解釈
function parseUTC(iso: string): Date {
  if (!iso) return new Date(NaN);
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso.replace(' ', 'T') + 'Z';
  return new Date(s);
}

function fmtTime(iso: string) {
  try {
    const d = parseUTC(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (isToday) return `${hh}:${mm}`;
    return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
  } catch { return ''; }
}


export const MessagesPage: React.FC<Props> = ({ user }) => {
  const [view, setView]         = useState<'list' | 'chat'>(() =>
    sessionStorage.getItem('crew_msg_view') === 'chat' ? 'chat' : 'list'
  );
  const [convUsers, setConvUsers] = useState<ConvUser[]>([]);
  const [selected, setSelected] = useState<ConvUser | null>(null);
  const [groupBy, setGroupBy]   = useState<'last' | 'vessel' | 'role'>('last');
  const [search, setSearch]     = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending]   = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [panelTab, setPanelTab]   = useState<'stamp' | 'emoji'>('stamp');
  const [msgType, setMsgType]     = useState<'general' | 'announcement' | 'urgent'>('general'); // 送信種別（通常/お知らせ/緊急）
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [menuId,    setMenuId]    = useState<string | null>(null); // アクションメニュー表示中のメッセージID
  const [editingId, setEditingId] = useState<string | null>(null); // 編集中のメッセージID
  const [editText,  setEditText]  = useState('');
  const bottomRef  = useRef<HTMLDivElement>(null);
  const pollRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 会話相手一覧ロード
  const loadConversations = async () => {
    // ① ユーザー一覧（新規送信先の選択用）。取得に失敗しても、下の会話相手だけは必ず出す。
    //    個人スマホで /users がタイムアウトすると一覧が空になり、共有端末では見える連絡が
    //    個人アプリだけ見えなくなっていた（2026-10-22 シーステージ様報告）。
    let allU: ConvUser[] = [];
    try {
      const usersRes = await apiFetch<any>('/users?limit=200');
      allU = (usersRes?.data ?? []).filter((u: any) => u.id !== user.user_id);
    } catch { /* 続行: 会話相手は convMap から出す */ }

    // ② 会話履歴（実際にやり取りした相手＋未読・プレビュー）
    const convMap: Record<string, ConvUser> = {};
    try {
      const convsRes = await apiFetch<any>('/messages/conversations');
      for (const c of (convsRes?.data ?? [])) {
        convMap[c.id] = c;
      }
    } catch { /* ignore */ }

    const all: ConvUser[] = allU.map((u: any) => ({
      ...u,
      last_message:  convMap[u.id]?.last_message  ?? null,
      last_at:       convMap[u.id]?.last_at        ?? null,
      last_type:     (convMap[u.id] as any)?.last_type ?? null,
      unread_count:  convMap[u.id]?.unread_count   ?? 0,
      unread_urgent:       (convMap[u.id] as any)?.unread_urgent       ?? 0,
      unread_announcement: (convMap[u.id] as any)?.unread_announcement ?? 0,
    }));
    // ユーザー一覧に載らない会話相手（システム通知、/users が取れなかったとき、
    // 権限で一覧に出ない管理者など）も必ず表示する。受信済みの連絡を取りこぼさないため、
    // convMap のうち all にまだ無い相手を全て追加する。
    const seen = new Set(all.map(u => u.id));
    for (const id of Object.keys(convMap)) {
      if (!seen.has(id)) all.push(convMap[id] as ConvUser);
    }
    all.sort((a, b) => {
      // 未読の緊急がある会話を最優先で上に
      const ua = (a.unread_urgent ?? 0) > 0 ? 1 : 0, ub = (b.unread_urgent ?? 0) > 0 ? 1 : 0;
      if (ua !== ub) return ub - ua;
      const ha = !!a.last_at, hb = !!b.last_at;
      if (ha && !hb) return -1;
      if (!ha && hb) return 1;
      if (ha && hb)  return b.last_at!.localeCompare(a.last_at!);
      return a.user_name.localeCompare(b.user_name);
    });
    setConvUsers(all);
    setLoadingList(false);

    // ページリロード後に選択状態を復元
    const savedId = sessionStorage.getItem('crew_msg_selected_id');
    if (savedId) {
      const match = all.find(u => u.id === savedId);
      if (match) setSelected(match);
    }
  };

  useEffect(() => { loadConversations(); }, []);

  // 連絡タブを見た時刻（__nav_badge__）を記録: 下部ナビの通知数バッジは一度タブを見たら消える。
  // 会話ごとの未読表示は従来どおり会話を開くまで残る（本来の既読管理は変えない）。
  useEffect(() => {
    apiFetch('/messages/mark-read', { method: 'POST', body: JSON.stringify({ partner_id: '__nav_badge__' }) }).catch(() => {});
  }, []);

  // チャット履歴ロード（silent=trueのときはローディング表示しない）
  const loadMessages = async (userId: string, silent = false) => {
    if (!silent) setLoadingMsgs(true);
    try {
      const res = await apiFetch<any>(`/messages?conversation_with=${userId}`);
      const newMsgs: Message[] = res?.data ?? [];
      setMessages(prev => {
        // 内容変化なしならstateを更新しない（ポーリング時のフラッシュ防止）
        if (prev.length === newMsgs.length &&
            prev.every((m, i) => m.id === newMsgs[i]?.id && m.is_read === newMsgs[i]?.is_read)) {
          return prev;
        }
        return newMsgs;
      });
      apiFetch('/messages/mark-read', { method: 'POST', body: JSON.stringify({ partner_id: userId }) }).catch(() => {});
    } catch { if (!silent) setMessages([]); }
    if (!silent) setLoadingMsgs(false);
  };

  useEffect(() => {
    if (!selected) return;
    loadMessages(selected.id);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMessages(selected.id, true), 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selected]);

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, [messages]);

  const openChat = (u: ConvUser) => {
    setSelected(u);
    setMessages([]);
    setInputText('');
    setView('chat');
    sessionStorage.setItem('crew_msg_selected_id', u.id);
    sessionStorage.setItem('crew_msg_view', 'chat');
    // そのユーザーとのDMを既読にする（loadMessagesでも呼ばれるが先行して呼ぶ）
    apiFetch('/messages/mark-read', { method: 'POST', body: JSON.stringify({ partner_id: u.id }) }).catch(() => {});
  };

  const handleSend = async () => {
    if (!inputText.trim() || sending || !selected) return;
    setSending(true);
    try {
      await apiFetch<unknown>('/messages', {
        method: 'POST',
        body: JSON.stringify({ content: inputText.trim(), message_type: msgType, recipient_user_id: selected.id }),
      });
      setInputText('');
      setMsgType('general'); // 誤送信防止のため送信後は通常に戻す
      await loadMessages(selected.id);
      loadConversations();
    } catch { /* ignore */ } finally { setSending(false); }
  };

  // ── 予約送信（指定日時にDMを自動送信）──────────────────────────
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleWhen, setScheduleWhen] = useState('');
  const [scheduled, setScheduled] = useState<any[]>([]);
  const loadScheduled = useCallback(() => {
    apiFetch<any>('/messages/scheduled').then(r => setScheduled(r?.data ?? [])).catch(() => {});
  }, []);
  useEffect(() => { loadScheduled(); }, [loadScheduled]);

  const handleSchedule = async () => {
    if (!inputText.trim() || !selected || !scheduleWhen) return;
    const dt = new Date(scheduleWhen);
    if (isNaN(dt.getTime()) || dt.getTime() < Date.now() + 30_000) { alert('1分以上先の日時を指定してください'); return; }
    setSending(true);
    try {
      await apiFetch<unknown>('/messages/bulk', {
        method: 'POST',
        body: JSON.stringify({
          content: inputText.trim(), message_type: msgType,
          target_type: 'user', target_value: selected.id, target_label: selected.user_name,
          scheduled_at: dt.toISOString(),
        }),
      });
      setInputText('');
      setMsgType('general');
      setShowSchedule(false);
      setScheduleWhen('');
      loadScheduled();
    } catch { alert('予約に失敗しました'); } finally { setSending(false); }
  };

  const cancelScheduled = async (id: string) => {
    if (!window.confirm('この予約送信を取り消しますか？')) return;
    try { await apiFetch(`/messages/scheduled/${id}`, { method: 'DELETE' }); loadScheduled(); } catch { /* ignore */ }
  };

  // 現在の会話相手あての未送信予約
  const pendingScheduled = selected
    ? scheduled.filter(s => s.status === 'pending' && s.target_type === 'user' && s.target_value === selected.id)
    : [];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── メッセージ編集・削除（自分の送信分のみ）────────────────────
  const startEdit = (m: Message) => { setMenuId(null); setEditingId(m.id); setEditText(m.content); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (m: Message) => {
    const t = editText.trim();
    if (!t || !selected) return;
    if (t === m.content) { cancelEdit(); return; }
    try {
      await apiFetch<unknown>(`/messages/${m.id}`, { method: 'PUT', body: JSON.stringify({ content: t }) });
      cancelEdit();
      await loadMessages(selected.id);
      loadConversations();
    } catch { alert('編集に失敗しました'); }
  };
  const handleDelete = async (m: Message) => {
    setMenuId(null);
    if (!selected) return;
    if (!window.confirm('このメッセージを削除しますか？')) return;
    try {
      await apiFetch<unknown>(`/messages/${m.id}`, { method: 'DELETE' });
      await loadMessages(selected.id);
      loadConversations();
    } catch { alert('削除に失敗しました'); }
  };

  const handleStampSend = async (stamp: string) => {
    if (sending || !selected) return;
    setShowPanel(false);
    setSending(true);
    try {
      await apiFetch<unknown>('/messages', {
        method: 'POST',
        body: JSON.stringify({ content: stamp, message_type: 'stamp', recipient_user_id: selected.id }),
      });
      await loadMessages(selected.id);
      loadConversations();
    } catch { /* ignore */ } finally { setSending(false); }
  };

  // ── 一覧画面 ──────────────────────────────────────────────────
  if (view === 'list') {
    // 検索フィルタ
    const filtered = search.trim()
      ? convUsers.filter(u =>
          u.user_name.includes(search) || u.employee_code?.includes(search)
        )
      : convUsers;

    // グループ化
    type Group = { label: string; users: ConvUser[] };
    let groups: Group[];
    if (groupBy === 'vessel') {
      const map = new Map<string, ConvUser[]>();
      for (const u of filtered) {
        const key = u.vessel_name ?? '船舶未配属';
        const arr = map.get(key) ?? [];
        arr.push(u);
        map.set(key, arr);
      }
      const namedKeys = [...map.keys()].filter(k => k !== '船舶未配属').sort((a, b) => a.localeCompare(b, 'ja'));
      const allKeys = map.has('船舶未配属') ? [...namedKeys, '船舶未配属'] : namedKeys;
      groups = allKeys.map(key => ({ label: key, users: map.get(key)!.sort((a, b) => a.user_name.localeCompare(b.user_name, 'ja')) }));
    } else if (groupBy === 'role') {
      const ROLE_ORDER = ['captain', 'crew', 'shore_admin', 'system_admin', 'admin'];
      const map = new Map<string, ConvUser[]>();
      for (const u of filtered) {
        const arr = map.get(u.user_role) ?? [];
        arr.push(u);
        map.set(u.user_role, arr);
      }
      const allKeys = [...new Set([...ROLE_ORDER, ...map.keys()])].filter(k => map.has(k));
      groups = allKeys.map(key => ({ label: ROLE_LABELS[key] ?? key, users: map.get(key)!.sort((a, b) => a.user_name.localeCompare(b.user_name, 'ja')) }));
    } else {
      groups = [{ label: '', users: filtered }];
    }

    const TAB_BTN = (label: string, val: 'last' | 'vessel' | 'role') => (
      <button
        onClick={() => setGroupBy(val)}
        style={{
          flex: 1, padding: '7px 0', fontSize: 12, fontWeight: groupBy === val ? 700 : 500,
          border: 'none', borderRadius: 8, cursor: 'pointer',
          background: groupBy === val ? '#0ea5e9' : 'transparent',
          color: groupBy === val ? 'white' : '#64748b',
          transition: 'all 0.15s',
        }}
      >{label}</button>
    );

    const UserRow = ({ u }: { u: ConvUser }) => {
      const hasUnread = (u.unread_count ?? 0) > 0;
      const hasUrgent = (u.unread_urgent ?? 0) > 0;
      const rowBg = hasUrgent ? '#fef2f2' : hasUnread ? '#f0f9ff' : 'white';
      return (
        <div
          key={u.id}
          onClick={() => openChat(u)}
          style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: rowBg, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', borderLeft: hasUrgent ? '4px solid #dc2626' : '4px solid transparent' }}
          onTouchStart={e => (e.currentTarget.style.background = hasUrgent ? '#fee2e2' : '#e0f2fe')}
          onTouchEnd={e   => (e.currentTarget.style.background = rowBg)}
        >
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <UserAvatar name={u.user_name} role={u.user_role} avatarPath={u.avatar_path} size={48} />
            {hasUnread && (
              <span style={{ position: 'absolute', top: -3, right: -3, minWidth: 18, height: 18, borderRadius: 9, background: '#ef4444', color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', boxSizing: 'border-box', border: '2px solid white' }}>
                {u.unread_count! > 99 ? '99+' : u.unread_count}
              </span>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span style={{ fontWeight: hasUnread ? 700 : 600, fontSize: 15, color: '#0f172a' }}>{u.user_name}</span>
                {hasUrgent && (
                  <span style={{ fontSize: 10, fontWeight: 800, background: '#dc2626', color: 'white', borderRadius: 10, padding: '2px 8px', flexShrink: 0 }}>🚨 緊急</span>
                )}
              </span>
              {u.last_at && <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtTime(u.last_at)}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              {groupBy !== 'role' && (
                <span style={{ fontSize: 10, background: '#f1f5f9', color: '#64748b', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>
                  {ROLE_LABELS[u.user_role] ?? u.user_role}
                </span>
              )}
              {groupBy !== 'vessel' && u.vessel_name && (
                <span style={{ fontSize: 10, background: '#e0f2fe', color: '#0369a1', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>
                  {u.vessel_name}
                </span>
              )}
              <span style={{ fontSize: 12, color: hasUrgent ? '#dc2626' : hasUnread ? '#0f172a' : '#94a3b8', fontWeight: hasUnread ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.last_type === 'urgent' || u.last_type === 'emergency' ? '🚨 ' : u.last_type === 'announcement' ? '📢 ' : ''}{u.last_message ?? 'メッセージなし'}
              </span>
            </div>
          </div>
          <span style={{ color: '#cbd5e1', fontSize: 18 }}>›</span>
        </div>
      );
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: '#f8fafc' }}>
        {/* ヘッダー */}
        <div className="app-header" style={{ background: 'white', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ padding: '0 16px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <BackButton size={38} />
            <span style={{ fontWeight: 900, fontSize: 20, color: '#0f172a' }}>連絡</span>
          </div>
          {/* 検索バー */}
          <div style={{ padding: '0 16px 10px' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="名前・社員コードで検索..."
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 20, padding: '8px 16px', fontSize: 14, outline: 'none', background: '#f8fafc', color: '#0f172a' }}
            />
          </div>
          {/* グループタブ */}
          <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', margin: '0 16px 12px', borderRadius: 10, padding: '4px' }}>
            {TAB_BTN('最近順', 'last')}
            {TAB_BTN('船名ごと', 'vessel')}
            {TAB_BTN('役職別', 'role')}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingList ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              {search ? '該当する連絡相手が見つかりません' : '連絡相手がいません'}
            </div>
          ) : groups.map((g, gi) => (
            <React.Fragment key={g.label || gi}>
              {g.label && (
                <div style={{ padding: '8px 16px 4px', fontSize: 12, fontWeight: 700, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', letterSpacing: 0.3 }}>
                  {g.label}
                  <span style={{ marginLeft: 8, fontWeight: 400, color: '#94a3b8' }}>{g.users.length}人</span>
                </div>
              )}
              {g.users.map(u => <UserRow key={u.id} u={u} />)}
            </React.Fragment>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }

  // ── チャット画面 ──────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: '#f8fafc' }}>
      {/* ヘッダー */}
      <div className="app-header" style={{
        background: 'white', padding: '0 16px 14px', borderBottom: '1px solid #e2e8f0',
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => { setView('list'); sessionStorage.removeItem('crew_msg_view'); sessionStorage.removeItem('crew_msg_selected_id'); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#0ea5e9', padding: 0, lineHeight: 1 }}>‹</button>
        {selected && (
          <>
            <UserAvatar name={selected.user_name} role={selected.user_role} avatarPath={selected.avatar_path} size={36} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{selected.user_name}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{ROLE_LABELS[selected.user_role] ?? selected.user_role}</div>
            </div>
          </>
        )}
      </div>

      {/* メッセージ一覧 */}
      <div onClick={() => { setShowPanel(false); setShowTypeMenu(false); }} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 6, background: '#eaf4fb' }}>
        {loadingMsgs ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>読み込み中...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✉️</div>
            まだメッセージはありません
          </div>
        ) : (() => {
          return messages.map((m) => {
            // 1対1チャットなので「送信者が会話相手でなければ自分」と判定する
            // システム通知の擬似会話は全メッセージが相手（システム）側
            const mine = selected?.id === '__system__' ? false
              : selected ? m.sender_user_id !== selected.id : m.sender_user_id === user.user_id;
            const showKidoku = mine && m.is_read;
            const deleted = !!m.deleted_at;
            const edited  = !!m.edited_at;
            const isEditing = editingId === m.id;
            const canEdit = mine && !deleted && m.message_type !== 'stamp';
            const canDelete = mine && !deleted;
            return (
              <div key={m.id} style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 6 }}>
                {!mine && (
                  <UserAvatar name={m.sender_name ?? '?'} role={selected?.user_role ?? 'crew'} avatarPath={selected?.avatar_path} size={32} />
                )}
                <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                  {!mine && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2, marginLeft: 4 }}>{m.sender_name}</div>}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flexDirection: mine ? 'row-reverse' : 'row' }}>
                    {deleted ? (
                      <div style={{
                        background: '#e5eaf0', color: '#94a3b8', padding: '10px 14px',
                        borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        fontSize: 13, fontStyle: 'italic', border: '1px dashed #cbd5e1',
                      }}>
                        🚫 メッセージを削除しました
                      </div>
                    ) : isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                        <textarea
                          autoFocus
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          rows={2}
                          style={{ border: '1.5px solid #06c755', borderRadius: 10, padding: '8px 12px', fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
                        />
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={cancelEdit} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#64748b' }}>キャンセル</button>
                          <button onClick={() => saveEdit(m)} disabled={!editText.trim()} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: editText.trim() ? '#06c755' : '#e2e8f0', color: editText.trim() ? '#fff' : '#94a3b8' }}>保存</button>
                        </div>
                      </div>
                    ) : m.message_type === 'stamp' ? (
                      <div
                        onClick={() => { if (canDelete) setMenuId(menuId === m.id ? null : m.id); }}
                        style={{ fontSize: 72, lineHeight: 1, userSelect: 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}
                      >
                        {m.content}
                      </div>
                    ) : (m.message_type === 'urgent' || m.message_type === 'emergency') ? (
                    /* 緊急: 赤枠強調＋ラベル（管理者からの緊急連絡を見落とさない） */
                    <div
                      onClick={() => { if (canEdit || canDelete) setMenuId(menuId === m.id ? null : m.id); }}
                      style={{
                        background: mine ? '#dc2626' : '#fef2f2',
                        color: mine ? 'white' : '#7f1d1d',
                        padding: '10px 14px',
                        borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        fontSize: 14, lineHeight: 1.5,
                        border: '2px solid #dc2626',
                        boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
                        wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 800, fontSize: 12, marginBottom: 4, color: mine ? '#fecaca' : '#dc2626' }}>
                        🚨 緊急
                      </div>
                      <span style={{ fontWeight: 600 }}>{m.content}</span>
                    </div>
                    ) : m.message_type === 'announcement' ? (
                    /* お知らせ: 控えめな青系強調 */
                    <div
                      onClick={() => { if (canEdit || canDelete) setMenuId(menuId === m.id ? null : m.id); }}
                      style={{
                        background: mine ? '#0ea5e9' : '#eff6ff',
                        color: mine ? 'white' : '#1e3a8a',
                        padding: '10px 14px',
                        borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        fontSize: 14, lineHeight: 1.5,
                        border: '1.5px solid #93c5fd',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                      }}>
                      <div style={{ fontWeight: 800, fontSize: 12, marginBottom: 4, color: mine ? '#dbeafe' : '#2563eb' }}>
                        📢 お知らせ
                      </div>
                      {m.content}
                    </div>
                    ) : (
                    <div
                      onClick={() => { if (canEdit || canDelete) setMenuId(menuId === m.id ? null : m.id); }}
                      style={{
                        background: mine ? '#06c755' : 'white',
                        color: mine ? 'white' : '#0f172a',
                        padding: '10px 14px',
                        borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        fontSize: 14, lineHeight: 1.5,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                      }}>
                      {m.content}
                    </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', gap: 1, flexShrink: 0, marginBottom: 2 }}>
                      {showKidoku && <span style={{ fontSize: 10, color: '#06c755', fontWeight: 600 }}>既読</span>}
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>{edited && !deleted ? '編集済み・' : ''}{fmtTime(m.created_at)}</span>
                    </div>
                  </div>
                  {/* 自分の送信メッセージ: タップで編集・削除メニュー */}
                  {menuId === m.id && (canEdit || canDelete) && !isEditing && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      {canEdit && (
                        <button onClick={() => startEdit(m)} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, border: '1px solid #e2e8f0', borderRadius: 16, background: '#fff', color: '#0f172a' }}>✏️ 編集</button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(m)} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, border: '1px solid #fecaca', borderRadius: 16, background: '#fff', color: '#dc2626' }}>🗑 削除</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          });
        })()}
        <div ref={bottomRef} />
      </div>

      {/* 入力エリア（システム通知は自動送信のみのため返信不可） */}
      {selected?.id === '__system__' ? (
        <div style={{ background: 'white', borderTop: '1px solid #e2e8f0', flexShrink: 0,
          padding: '14px 16px max(14px, env(safe-area-inset-bottom))', textAlign: 'center',
          fontSize: 12, color: '#94a3b8' }}>
          システムからの自動通知です（返信はできません）
        </div>
      ) : (
      <div style={{ background: 'white', borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
        {/* スタンプ／絵文字パネル */}
        {showPanel && (
          <div onClick={e => e.stopPropagation()} style={{ borderBottom: '1px solid #f1f5f9' }}>
            {/* タブ */}
            <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9' }}>
              {(['stamp', 'emoji'] as const).map(tab => (
                <button key={tab} onClick={() => setPanelTab(tab)} style={{
                  flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 600,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: panelTab === tab ? '#0ea5e9' : '#94a3b8',
                  borderBottom: panelTab === tab ? '2px solid #0ea5e9' : '2px solid transparent',
                  transition: 'all 0.12s',
                }}>
                  {tab === 'stamp' ? 'スタンプ' : '絵文字'}
                </button>
              ))}
            </div>
            {/* スタンプ */}
            {panelTab === 'stamp' && (
              <div style={{ maxHeight: 210, overflowY: 'auto', padding: '10px 12px 8px',
                display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2 }}>
                {STAMPS.map(s => (
                  <button key={s} onClick={() => handleStampSend(s)} style={{
                    fontSize: 34, lineHeight: 1, background: 'none', border: 'none',
                    borderRadius: 10, padding: '6px 0', cursor: 'pointer',
                  }}
                  onTouchStart={e => { e.currentTarget.style.background = '#f0f9ff'; }}
                  onTouchEnd={e   => { e.currentTarget.style.background = 'none'; }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0f9ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  >{s}</button>
                ))}
              </div>
            )}
            {/* 絵文字ピッカー */}
            {panelTab === 'emoji' && (
              <div onClick={e => e.stopPropagation()}>
                <EmojiPicker
                  onEmojiClick={(data: EmojiClickData) => {
                    setInputText(prev => prev + data.emoji);
                  }}
                  emojiStyle={EmojiStyle.NATIVE}
                  theme={Theme.LIGHT}
                  height={300}
                  width="100%"
                  searchPlaceholder="絵文字を検索..."
                  previewConfig={{ showPreview: false }}
                  lazyLoadEmojis
                />
              </div>
            )}
          </div>
        )}
        {/* 予約済み送信の帯（この会話あて・未送信のみ） */}
        {pendingScheduled.length > 0 && (
          <div style={{ padding: '6px 12px', background: '#fffbeb', borderTop: '1px solid #fde68a', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pendingScheduled.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#92400e' }}>
                <span>⏰ {new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s.scheduled_at) ? s.scheduled_at : s.scheduled_at.replace(' ', 'T') + 'Z').toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo' })} に送信予定</span>
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#b45309' }}>{s.content}</span>
                <button onClick={() => cancelScheduled(s.id)}
                  style={{ border: 'none', background: 'none', color: '#dc2626', fontWeight: 700, fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>取消</button>
              </div>
            ))}
          </div>
        )}

        {/* 予約日時指定モーダル */}
        {showSchedule && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowSchedule(false)}>
            <div onClick={e => e.stopPropagation()}
              style={{ width: 320, maxWidth: '90vw', background: 'white', borderRadius: 16, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 4 }}>⏰ 予約送信</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>指定した日時に自動でメッセージを送信します</div>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                {inputText.trim() || '（メッセージを入力してください）'}
              </div>
              <input type="datetime-local" value={scheduleWhen} onChange={e => setScheduleWhen(e.target.value)}
                min={new Date(Date.now() + 5 * 60000 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                style={{ width: '100%', padding: '10px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 15, marginBottom: 14, boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowSchedule(false)}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>キャンセル</button>
                <button onClick={handleSchedule} disabled={sending || !inputText.trim() || !scheduleWhen}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: (!inputText.trim() || !scheduleWhen) ? '#cbd5e1' : '#f59e0b', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>予約する</button>
              </div>
            </div>
          </div>
        )}

        {/* 送信種別バナー（通常以外を選択中のみ表示） */}
        {msgType !== 'general' && (
          <div onClick={() => setMsgType('general')}
            style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: msgType === 'urgent' ? '#fef2f2' : '#eff6ff',
              color: msgType === 'urgent' ? '#dc2626' : '#2563eb',
              borderTop: '1px solid ' + (msgType === 'urgent' ? '#fecaca' : '#bfdbfe'),
            }}>
            {msgType === 'urgent' ? '🚨 緊急メッセージとして送信します' : '📢 お知らせとして送信します'}
            <span style={{ fontWeight: 400, marginLeft: 6 }}>（タップで通常に戻す）</span>
          </div>
        )}

        {/* 入力行 */}
        <div style={{
          padding: '8px 10px',
          paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
          display: 'flex', alignItems: 'flex-end', gap: 6,
        }}>
          {/* 種別選択（通常/お知らせ/緊急） */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => { textareaRef.current?.blur(); setShowPanel(false); setShowTypeMenu(v => !v); }}
              title="送信種別"
              style={{
                width: 36, height: 36, borderRadius: '50%',
                border: '1.5px solid ' + (msgType === 'urgent' ? '#fecaca' : msgType === 'announcement' ? '#bfdbfe' : '#e2e8f0'),
                background: msgType === 'urgent' ? '#fef2f2' : msgType === 'announcement' ? '#eff6ff' : 'white',
                fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, padding: 0,
              }}>
              {msgType === 'urgent' ? '🚨' : msgType === 'announcement' ? '📢' : '💬'}
            </button>
            {showTypeMenu && (
              <div style={{
                position: 'absolute', bottom: 44, left: 0, zIndex: 100, width: 170,
                background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.18)', overflow: 'hidden',
              }}>
                {([
                  ['general',      '💬', '通常',    '#0f172a'],
                  ['announcement', '📢', 'お知らせ', '#2563eb'],
                  ['urgent',       '🚨', '緊急',    '#dc2626'],
                ] as const).map(([key, icon, lbl, color]) => (
                  <button key={key} onClick={() => { setMsgType(key); setShowTypeMenu(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 14px',
                      border: 'none', background: msgType === key ? '#f8fafc' : 'white',
                      cursor: 'pointer', fontSize: 13, fontWeight: 700, color,
                    }}>
                    <span style={{ fontSize: 16 }}>{icon}</span>{lbl}
                    {msgType === key && <span style={{ marginLeft: 'auto', color: '#06c755' }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* テキスト入力 */}
          <textarea
            ref={textareaRef}
            rows={1}
            value={inputText}
            onChange={e => { setInputText(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder="メッセージを入力"
            style={{
              flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 22,
              padding: '9px 14px', fontSize: 15, resize: 'none', outline: 'none',
              background: '#f8fafc', color: '#0f172a', lineHeight: 1.45,
              maxHeight: 96, overflowY: 'auto', fontFamily: 'inherit',
            }}
            onFocus={e => { e.target.style.borderColor = '#0ea5e9'; setShowPanel(false); setShowTypeMenu(false); }}
            onBlur={e  => { e.target.style.borderColor = '#e2e8f0'; }}
          />
          {/* フェイスボタン（常時表示） */}
          <button
            onClick={() => { textareaRef.current?.blur(); setShowPanel(v => !v); }}
            style={{
              width: 36, height: 36, background: 'none', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0, padding: 0,
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
              stroke={showPanel ? '#0ea5e9' : '#b0bec5'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8.5 14.5s1 1.5 3.5 1.5 3.5-1.5 3.5-1.5"/>
              <circle cx="9" cy="10" r="1.1" fill={showPanel ? '#0ea5e9' : '#b0bec5'} stroke="none"/>
              <circle cx="15" cy="10" r="1.1" fill={showPanel ? '#0ea5e9' : '#b0bec5'} stroke="none"/>
            </svg>
          </button>
          {/* 予約送信ボタン（入力あり時のみ表示） */}
          {inputText.trim() && (
            <button onClick={() => { setScheduleWhen(''); setShowSchedule(true); }} title="予約送信"
              style={{
                width: 42, height: 42, borderRadius: '50%', border: '1.5px solid #fde68a',
                background: '#fffbeb', color: '#d97706', fontSize: 18, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>⏰</button>
          )}
          {/* 送信ボタン（入力あり時のみ表示） */}
          {inputText.trim() && (
            <button onClick={handleSend} disabled={sending} style={{
              width: 42, height: 42, borderRadius: '50%', border: 'none',
              background: msgType === 'urgent' ? '#dc2626' : msgType === 'announcement' ? '#0ea5e9' : '#06c755',
              color: 'white', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            }}>➤</button>
          )}
        </div>
      </div>
      )}
      <BottomNav />
    </div>
  );
};
